import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import type { CSSProperties } from 'react';
import { Fragment } from 'react';
import type { MessageDescriptor } from 'react-intl';
import { FormattedMessage, FormattedNumber, defineMessages } from 'react-intl';
import styled from 'styled-components';

import {
  CardQuality,
  CraftGameplayTier,
} from '@sorare/core/src/__generated__/globalTypes';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { LabelM, LabelS } from '@sorare/core/src/atoms/typography';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useFeatureFlags } from '@sorare/core/src/hooks/useFeatureFlags';
import { GAMEPLAY_TIER_ORDER } from '@sorare/core/src/lib/gameplayTiers';
import { glossary } from '@sorare/core/src/lib/glossary';
import { formatWord } from '@sorare/core/src/lib/humanize';

import type { OddsTable_cardShardsChest } from './__generated__/index.graphql.ts';

const Wrapper = styled(Vertical)`
  align-self: stretch;
  border: 1px solid var(--c-nd-150);
  border-radius: var(--unit);
`;

const TableHeader = styled.div`
  display: grid;
  grid-template-columns: repeat(var(--nb-columns), minmax(0, 1fr));
  background: var(--c-nd-50);
`;

const TableBody = styled.div`
  display: grid;
  grid-template-columns: repeat(var(--nb-columns), minmax(0, 1fr));
`;

const HeaderCell = styled(LabelS).attrs({
  color: 'var(--c-nd-600)',
})`
  padding: var(--half-unit) var(--unit);
`;

const Cell = styled(LabelM)`
  padding: var(--unit);
  border-bottom: 1px solid var(--c-nd-100);
`;

const gameplayTierLabels = defineMessages<
  CraftGameplayTier | (typeof CardQuality)[keyof typeof CardQuality]
>({
  [CraftGameplayTier.GOAT]: { defaultMessage: 'Icon' },
  [CraftGameplayTier.STAR]: { defaultMessage: 'Star' },
  [CraftGameplayTier.IMPACT]: { defaultMessage: 'Key' },
  [CraftGameplayTier.ROSTER]: { defaultMessage: 'Roster' },
  [CraftGameplayTier.DNP]: { defaultMessage: 'Reserve' },
  [CraftGameplayTier.STAR_OR_GOAT]: { defaultMessage: 'Star or Icon' },
  [CardQuality.TIER_0]: { defaultMessage: 'Star' },
  [CardQuality.TIER_1]: { defaultMessage: 'Tier 1' },
  [CardQuality.TIER_2]: { defaultMessage: 'Tier 2' },
  [CardQuality.TIER_3]: { defaultMessage: 'Tier 3' },
  [CardQuality.TIER_4]: { defaultMessage: 'Tier 4' },
  [CardQuality.TIER_5]: { defaultMessage: 'Tier 5' },
});

type Props = {
  cardShardsChest: OddsTable_cardShardsChest;
};

export const OddsTable = ({ cardShardsChest }: Props) => {
  const {
    flags: {
      craftingWithGameplayTier = {
        football: false,
        nba: false,
        baseball: false,
      },
    },
  } = useFeatureFlags();
  const { sport } = useSportContext();
  const { thresholds } = cardShardsChest;
  const isGameplayTierEnabled = !!craftingWithGameplayTier[sport.toLowerCase()];

  // When the chest config uses the STAR_OR_GOAT alias, the backend exposes a
  // single combined slot covering both tiers, so we hide the standalone STAR
  // and GOAT rows and surface a dedicated "Star or Icon" row instead.
  const hasStarOrGoatRow =
    isGameplayTierEnabled &&
    thresholds.some(threshold =>
      threshold.draws.some(draw =>
        draw.drawProbabilities.some(
          p => p.quality === CraftGameplayTier.STAR_OR_GOAT
        )
      )
    );

  const rows: { key: string; label: MessageDescriptor }[] =
    isGameplayTierEnabled
      ? [
          ...(hasStarOrGoatRow
            ? [
                {
                  key: CraftGameplayTier.STAR_OR_GOAT,
                  label: gameplayTierLabels[CraftGameplayTier.STAR_OR_GOAT],
                },
              ]
            : []),
          ...GAMEPLAY_TIER_ORDER.filter(tier =>
            hasStarOrGoatRow
              ? tier !== CraftGameplayTier.STAR &&
                tier !== CraftGameplayTier.GOAT
              : true
          ).map(tier => ({
            key: tier,
            label: gameplayTierLabels[tier],
          })),
        ]
      : Object.values(CardQuality).map(quality => ({
          key: quality,
          label: gameplayTierLabels[quality],
        }));

  const getDrawProbabilities = (
    draw: (typeof thresholds)[number]['draws'][number]
  ) => (isGameplayTierEnabled ? draw.drawProbabilities : draw.probabilities);

  return (
    <Wrapper
      style={
        {
          '--nb-columns': thresholds.length + 1,
        } as CSSProperties
      }
    >
      <TableHeader>
        <HeaderCell>
          <FormattedMessage {...glossary.tier} />
        </HeaderCell>
        {thresholds.map(threshold => (
          <HeaderCell key={threshold.name}>
            {formatWord(threshold.name)}
          </HeaderCell>
        ))}
      </TableHeader>
      <TableBody>
        {rows.map(row => (
          <Fragment key={row.key}>
            <Cell>
              <LabelS color="var(--c-nd-600)">
                <FormattedMessage {...row.label} />
              </LabelS>
            </Cell>
            {thresholds.map(threshold => {
              const draws = threshold.draws
                .map(draw =>
                  getDrawProbabilities(draw).find(p => p.quality === row.key)
                )
                .filter(Boolean);
              const deterministicDraw = threshold.draws.find(draw =>
                getDrawProbabilities(draw).some(
                  p => p.basisPoints === 10000 && p.quality === row.key
                )
              );
              if (deterministicDraw) {
                return (
                  <Cell key={threshold.name}>
                    <FormattedMessage
                      defaultMessage="{count, plural, one {1 Card} other {# cards}}: {draw}<br></br>Rest: 0%"
                      values={{
                        count: deterministicDraw.nbCards,
                        draw: (
                          <FormattedNumber
                            value={
                              (getDrawProbabilities(deterministicDraw).at(0)
                                ?.basisPoints || 0) / 10000
                            }
                            style="percent"
                            maximumFractionDigits={1}
                          />
                        ),
                        br: () => <br />,
                      }}
                    />
                  </Cell>
                );
              }
              return (
                <Cell key={threshold.name}>
                  <FormattedNumber
                    value={(draws[0]?.basisPoints || 0) / 10000}
                    style="percent"
                    maximumFractionDigits={1}
                  />
                </Cell>
              );
            })}
          </Fragment>
        ))}
      </TableBody>
    </Wrapper>
  );
};

OddsTable.fragments = {
  cardShardsChest: gql`
    fragment OddsTable_cardShardsChest on CardShardsChest {
      id
      thresholds {
        name
        draws {
          nbCards
          probabilities {
            basisPoints
            quality
          }
          drawProbabilities {
            basisPoints
            quality
          }
        }
      }
    }
  ` as TypedDocumentNode<OddsTable_cardShardsChest>,
};
